import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { apiFetchGenAISuggestion } from '../fetchers';
// import { removeExpiredQuery } from '../utils';

// Styled component for the box
const BoxContainer = styled.div`
  border: 2px solid #ccc;
  padding: 20px;
  border-radius: 5px;
  background-color: #f0f0f0;
`;

// Styled component for the text inside the box
const Text = styled.p`
  color: #333;
  font-size: 16px;
`;

/**
 * Component for displaying a suggestion based on the query text and API results.
 *
 * @todo replace dangerouslySetInnerHTML with a safer alternative to sanitize HTML
 * @todo not used in current configuration @date 2024-12-03
 * @see resultsConfiguration in src/containers/Kendra/configuration.js
 *
 * @example
 * const queryText = "sample query"
 * const retrieveAPI = { ... }
 * const queryAPI = { ... }
 * const selectedFacets = { ... }
 * return <BoxWithText queryText={queryText} retrieveAPI={retrieveAPI} queryAPI={queryAPI} selectedFacets={selectedFacets} />
 *
 * @param {Object} props - The component props.
 * @param {string} props.queryText - The query text.
 * @param {Object} props.retrieveAPI - The API response for retrieve.
 * @param {Object} props.queryAPI - The API response for query.
 * @param {Object} props.selectedFacets - The selected facets for filtering.
 * @returns {JSX.Element|null} The rendered BoxWithText component or null if no suggestion is available.
 */
const BoxWithText = ({ queryText, retrieveAPI, queryAPI, selectedFacets }) => {
  const [suggestion, setSuggestion] = useState(null);
  const prevResults = useRef(retrieveAPI);
  const prevQueryText = useRef(queryText);
  const prevSelectedFacets = useRef(selectedFacets);

  /**
   * Searches the query in the search logs.
   *
   * @param {string} _queryString - The query string to search for.
   * @param {Array} _searchLogs - The array of search logs.
   * @returns {string|null} The matching response or null if no match is found.
   */
  const searchQuery = (_queryString, _searchLogs)=> {
    // Find the object with matching queryText
    const matchingLog =
      _searchLogs.find(log => log.queryText.includes(_queryString));

    // If a match is found, return the corresponding response
    if (matchingLog) {
      return matchingLog.response;
    }

    // If no match is found, return null
    return null;
  }
  
  useEffect(() => {
    let searchLogs = localStorage.getItem('searchLog');
    searchLogs = searchLogs ? JSON.parse(searchLogs) : [];
    setSuggestion('Loading...');

    /**
     * Gets the score value based on the score confidence.
     *
     * @param {string} scoreConfidence - The score confidence.
     * @returns {number} The score value.
     */
    const getScoreValue = scoreConfidence => {
      switch (scoreConfidence) {
        case 'VERY_HIGH':
          return 5
        case 'HIGH':
          return 4
        case 'MEDIUM':
          return 3
        case 'LOW':
          return 2
        case 'NOT_AVAILABLE':
          return 1
        default:
          return 0
      }
    }

    /**
     * Removes specific keys from the object.
     *
     * @param {Object} obj - The object to remove keys from.
     * @returns {Object} The new object with specific keys removed.
     */
    const removeKeys = obj => {
      // Removing keys to squeeze more documents into the context window for LLM analysis.
      const newObj = {}
      for (const key in obj) {
        if (
          key !== 'FeedbackToken' &&
          key !== 'Type' &&
          key !== 'Format' &&
          key !== 'AdditionalAttributes' &&
          key !== 'Id' &&
          key !== 'DocumentId'
        ) {
          newObj[key] = obj[key]
        }
      }
      return newObj
    }

    /**
     * Sorts the search results by score.
     *
     * @param {Object} _retrieveAPI - The retrieve API response.
     * @param {Object} _queryAPI - The query API response.
     * @returns {Object} The sorted and filtered search results.
     */
    const sortSearchResultsByScore = (_retrieveAPI, _queryAPI) => {

      const combinedResults = [
        ..._retrieveAPI.ResultItems,
        ..._queryAPI.ResultItems,
      ]
      const sortedResults = combinedResults.sort((a, b) => {
        const aScore = getScoreValue(a.ScoreAttributes.ScoreConfidence)
        const bScore = getScoreValue(b.ScoreAttributes.ScoreConfidence)
  
        return bScore - aScore
      })
  
      if (
        sortedResults.length > 0 &&
        sortedResults[0].ScoreAttributes.ScoreConfidence === 'VERY_HIGH'
      ) {
        return removeKeys(sortedResults.slice(0, 6))
      }

      return removeKeys(sortedResults.slice(0, 8))
    }

    /**
     * Fetches the suggestion from the API.
     *
     * @param {Array} _searchLogs - The array of search logs.
     */
    const fetchSuggestion = async _searchLogs => {
      try {
        const combinedResults = sortSearchResultsByScore(retrieveAPI, queryAPI);
        const response = await apiFetchGenAISuggestion({
          ResultItems: combinedResults,
          query: queryText,
          filters: selectedFacets
        });
        if (response?.errorType === 'LambdaTimeoutError') {
          setSuggestion(null);
        } else {
          if (response.source === 'LLM' || response.source === 'vectorstore') {
            const newLog = {
              queryText,
              firstResult: combinedResults[0],
              response: response.answer,
              filters: selectedFacets,
            };
            _searchLogs.push(newLog);
            localStorage.setItem('searchLog', JSON.stringify(_searchLogs));
          }
          setSuggestion(response.answer);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
  
    // const shouldFetchSuggestion =
    //   results !== prevResults.current ||
    //   queryText !== prevQueryText.current ||
    //   selectedFacets !== prevSelectedFacets.current;

    const matchedQueryText = searchQuery(queryText, searchLogs)
    if (matchedQueryText) {
      setSuggestion(matchedQueryText)
      return
    }
  
    if (queryText && retrieveAPI && queryAPI) {
      fetchSuggestion(searchLogs);
    }
  
    prevResults.current = retrieveAPI;
    prevQueryText.current = queryText;
    prevSelectedFacets.current = selectedFacets;
  }, [queryText, retrieveAPI, queryAPI, selectedFacets]);


  return (
    suggestion ? (
      <div>
        {suggestion === 'Loading...' ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img src="img/giphy.gif" alt="Loading..." width="100" height="100" />
            <p style={{ marginLeft: '10px' }}>GenAI model is thinking...</p>
          </div>
        ) : (
          <BoxContainer>
            <Text dangerouslySetInnerHTML={{ __html: suggestion }} />
          </BoxContainer>
        )}
      </div>
    ) : null
  );
};

export default BoxWithText;
